import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Lock, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  if (user) {
    navigate("/admin/dashboard");
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    setLoading(true);
    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      toast.success("Connexion réussie !");
      navigate("/admin/dashboard");
    } else {
      toast.error(result.error || "Identifiants incorrects");
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
        data-testid="admin-login-container"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#0066FF] flex items-center justify-center mx-auto mb-4">
            <Zap className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-['Outfit'] text-2xl font-bold uppercase" data-testid="login-title">
            Admin APM
          </h1>
          <p className="text-[#94A3B8] mt-2">
            Connectez-vous pour accéder au tableau de bord
          </p>
        </div>

        {/* Form */}
        <form 
          onSubmit={handleSubmit} 
          className="bg-[#0F0F16] border border-white/10 p-8 space-y-6"
          data-testid="login-form"
        >
          <div>
            <Label htmlFor="email" className="form-label flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@apm.fr"
              className="form-input bg-[#05050A] border-white/10 text-white placeholder:text-[#64748B] focus:border-[#0066FF] h-12"
              data-testid="login-email"
            />
          </div>

          <div>
            <Label htmlFor="password" className="form-label flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Mot de passe
            </Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="form-input bg-[#05050A] border-white/10 text-white placeholder:text-[#64748B] focus:border-[#0066FF] h-12"
              data-testid="login-password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="login-submit"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Connexion...
              </>
            ) : (
              "Se connecter"
            )}
          </button>
        </form>

        {/* Back link */}
        <div className="text-center mt-6">
          <a
            href="/"
            className="text-[#94A3B8] hover:text-white text-sm transition-colors"
            data-testid="back-to-site"
          >
            ← Retour au site
          </a>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
